import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Ban,
  Download,
  Eye,
  History,
  IdCard,
  LayoutTemplate,
  Loader2,
  RefreshCw,
  RotateCw,
} from 'lucide-react';
import { useSchool } from '../../../context/SchoolContext';
import { idCardApi } from '../services/idCardApi';
import {
  downloadBlobResponse,
  formatDateTime,
  getApiErrorMessage,
  humanize,
  jobIsRunning,
  unwrapApiResponse,
} from '../utils/documentUtils';
import {
  EmptyState,
  ErrorBanner,
  LoadingBlock,
  Modal,
  PageHeader,
  Pagination,
  StatusBadge,
} from '../components/DocumentUi';

const PAGE_SIZE = 20;
const JOB_STATUSES = ['PENDING', 'PROCESSING', 'COMPLETED', 'PARTIAL_FAILED', 'FAILED', 'CANCELLED'];

const GenerationJobsPage = () => {
  const location = useLocation();
  const { schoolId } = useSchool();
  const createdJobId = location.state?.createdJobId || '';

  const [jobs, setJobs] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [documentType, setDocumentType] = useState('');
  const [jobStatus, setJobStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [detailJob, setDetailJob] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState('');
  const [downloadingId, setDownloadingId] = useState('');

  const hasRunningJobs = useMemo(
    () => jobs.some((job) => jobIsRunning(job.job_status)),
    [jobs],
  );

  const loadJobs = useCallback(async (silent = false) => {
    if (!schoolId) return;
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const response = await idCardApi.getGenerationJobs(schoolId, {
        page,
        page_size: PAGE_SIZE,
        document_type: documentType,
        job_status: jobStatus,
      });
      const result = unwrapApiResponse(response);
      setJobs(result.data || []);
      setPagination(result.pagination);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Failed to load generation jobs.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [documentType, jobStatus, page, schoolId]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    if (!hasRunningJobs) return undefined;
    const timer = window.setInterval(() => loadJobs(true), 5000);
    return () => window.clearInterval(timer);
  }, [hasRunningJobs, loadJobs]);

  const openJob = async (job) => {
    setDetailJob(job);
    setDetailLoading(true);
    setDocuments([]);
    setError('');
    try {
      const [jobResponse, documentsResponse] = await Promise.all([
        idCardApi.getGenerationJob(schoolId, job.id),
        idCardApi.getGeneratedDocuments(schoolId, { job_id: job.id, page: 1, page_size: 100 }),
      ]);
      setDetailJob(unwrapApiResponse(jobResponse).data || job);
      setDocuments(unwrapApiResponse(documentsResponse).data || []);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Failed to load generation job details.'));
    } finally {
      setDetailLoading(false);
    }
  };

  const cancelJob = async (job) => {
    if (!window.confirm(`Cancel generation job ${job.id.slice(0, 8)}?`)) return;
    setCancellingId(job.id);
    setError('');
    try {
      await idCardApi.cancelGenerationJob(schoolId, job.id);
      await loadJobs(true);
      if (detailJob?.id === job.id) await openJob(job);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Could not cancel this generation job.'));
    } finally {
      setCancellingId('');
    }
  };

  const downloadDocument = async (document) => {
    setDownloadingId(document.id);
    setError('');
    try {
      const response = await idCardApi.downloadGeneratedDocument(schoolId, document.id);
      const fallbackExtension = document.owner_type === 'ZIP' ? 'zip' : 'pdf';
      downloadBlobResponse(response, `${document.document_no || document.id}.${fallbackExtension}`);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Could not download the generated file.'));
    } finally {
      setDownloadingId('');
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <PageHeader
        title="Generation Jobs"
        description="Monitor individual and bulk ID-card jobs, inspect per-record failures, cancel active work, and download generated outputs."
        actions={
          <>
            <Link to="/documents/id-cards" className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"><IdCard className="h-4 w-4" /> Generate</Link>
            <Link to="/documents/templates" className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"><LayoutTemplate className="h-4 w-4" /> Templates</Link>
            <Link to="/documents/card-history" className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"><History className="h-4 w-4" /> History</Link>
          </>
        }
      />

      {createdJobId && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
          Generation job <span className="font-mono font-semibold">{createdJobId}</span> was created. Active jobs refresh automatically every five seconds.
        </div>
      )}
      <ErrorBanner message={error} onDismiss={() => setError('')} />

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="grid gap-3 md:grid-cols-3">
          <select value={documentType} onChange={(event) => { setDocumentType(event.target.value); setPage(1); }} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
            <option value="">All ID-card types</option>
            <option value="STUDENT_ID_CARD">Student ID cards</option>
            <option value="STAFF_ID_CARD">Staff ID cards</option>
          </select>
          <select value={jobStatus} onChange={(event) => { setJobStatus(event.target.value); setPage(1); }} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
            <option value="">All job statuses</option>
            {JOB_STATUSES.map((status) => <option key={status} value={status}>{humanize(status)}</option>)}
          </select>
          <button type="button" onClick={() => loadJobs(true)} disabled={refreshing} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh now
          </button>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <LoadingBlock label="Loading generation jobs..." />
        ) : jobs.length === 0 ? (
          <EmptyState title="No generation jobs found" description="Create a student or staff ID-card job from the Generate screen." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-5 py-3">Job</th>
                  <th className="px-5 py-3">Type / template</th>
                  <th className="px-5 py-3">Progress</th>
                  <th className="px-5 py-3">Result</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Created</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {jobs.map((job) => {
                  const highlighted = job.id === createdJobId;
                  return (
                    <tr key={job.id} className={highlighted ? 'bg-blue-50/60' : 'hover:bg-gray-50'}>
                      <td className="px-5 py-4"><p className="font-mono text-xs font-semibold text-gray-900">{job.id.slice(0, 12)}…</p><p className="mt-1 text-xs text-gray-500">{humanize(job.output_mode)}</p></td>
                      <td className="px-5 py-4"><p className="font-medium text-gray-900">{humanize(job.document_type)}</p><p className="mt-1 text-xs text-gray-500">{job.template?.name || 'Default template'}</p></td>
                      <td className="min-w-48 px-5 py-4"><div className="mb-1 flex items-center justify-between text-xs text-gray-500"><span>{job.processed_records}/{job.total_records}</span><span>{job.progress_percentage}%</span></div><div className="h-2 overflow-hidden rounded-full bg-gray-100"><div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${Math.max(0, Math.min(100, job.progress_percentage || 0))}%` }} /></div></td>
                      <td className="px-5 py-4 text-xs text-gray-600"><p className="text-emerald-700">{job.successful_records} successful</p><p className="mt-1 text-red-600">{job.failed_records} failed</p></td>
                      <td className="px-5 py-4"><StatusBadge value={job.job_status} /></td>
                      <td className="px-5 py-4 text-gray-600">{formatDateTime(job.created_at)}</td>
                      <td className="px-5 py-4"><div className="flex justify-end gap-2"><button type="button" onClick={() => openJob(job)} className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50" title="View job"><Eye className="h-4 w-4" /></button>{jobIsRunning(job.job_status) && <button type="button" disabled={cancellingId === job.id} onClick={() => cancelJob(job)} className="rounded-lg border border-red-200 p-2 text-red-600 hover:bg-red-50 disabled:opacity-50" title="Cancel job">{cancellingId === job.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}</button>}</div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Pagination pagination={pagination} page={page} onPageChange={setPage} disabled={loading} />
      </section>

      <Modal
        open={Boolean(detailJob)}
        onClose={() => setDetailJob(null)}
        title={detailJob ? `Generation job ${detailJob.id.slice(0, 12)}…` : 'Generation job'}
        description="Progress, failures, request details, and generated output files."
        size="max-w-5xl"
        footer={detailJob && jobIsRunning(detailJob.job_status) ? <div className="flex justify-end"><button type="button" disabled={cancellingId === detailJob.id} onClick={() => cancelJob(detailJob)} className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"><Ban className="h-4 w-4" /> Cancel job</button></div> : null}
      >
        {detailLoading ? (
          <LoadingBlock label="Loading job details..." />
        ) : detailJob ? (
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl bg-gray-50 p-4"><p className="text-xs font-semibold uppercase text-gray-500">Status</p><div className="mt-2"><StatusBadge value={detailJob.job_status} /></div></div>
              <div className="rounded-xl bg-gray-50 p-4"><p className="text-xs font-semibold uppercase text-gray-500">Progress</p><p className="mt-2 text-xl font-bold text-gray-900">{detailJob.progress_percentage}%</p></div>
              <div className="rounded-xl bg-gray-50 p-4"><p className="text-xs font-semibold uppercase text-gray-500">Successful</p><p className="mt-2 text-xl font-bold text-emerald-700">{detailJob.successful_records}</p></div>
              <div className="rounded-xl bg-gray-50 p-4"><p className="text-xs font-semibold uppercase text-gray-500">Failed</p><p className="mt-2 text-xl font-bold text-red-700">{detailJob.failed_records}</p></div>
            </div>

            {detailJob.error_message && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{detailJob.error_message}</div>}

            <section>
              <div className="mb-3 flex items-center justify-between"><h3 className="font-bold text-gray-900">Generated files</h3>{jobIsRunning(detailJob.job_status) && <span className="inline-flex items-center gap-2 text-xs font-medium text-blue-600"><RotateCw className="h-3.5 w-3.5 animate-spin" /> Processing</span>}</div>
              {documents.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">No output files are available yet.</div>
              ) : (
                <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
                  {documents.map((document) => (
                    <div key={document.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"><div><p className="font-medium text-gray-900">{document.title || document.document_no || document.id}</p><p className="mt-1 text-xs text-gray-500">{humanize(document.owner_type)} · {humanize(document.document_type)} · {formatDateTime(document.created_at)}</p></div><button type="button" disabled={!document.file_url || downloadingId === document.id} onClick={() => downloadDocument(document)} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">{downloadingId === document.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download</button></div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h3 className="mb-3 font-bold text-gray-900">Failures</h3>
              {detailJob.failures?.length ? (
                <div className="space-y-2">{detailJob.failures.map((failure) => <div key={failure.id} className="rounded-lg border border-red-200 bg-red-50 p-3"><div className="flex flex-wrap items-center justify-between gap-2"><p className="font-medium text-red-900">{failure.owner_name || failure.owner_id}</p><span className="text-xs font-semibold text-red-600">{humanize(failure.error_code)}</span></div><p className="mt-1 text-sm text-red-700">{failure.error_message}</p></div>)}</div>
              ) : <p className="rounded-lg border border-dashed border-gray-300 p-5 text-center text-sm text-gray-500">No per-record failures.</p>}
            </section>

            <section className="grid gap-4 lg:grid-cols-2">
              <div><h3 className="mb-2 font-bold text-gray-900">Request payload</h3><pre className="max-h-64 overflow-auto rounded-xl bg-gray-950 p-4 text-xs text-gray-100">{JSON.stringify(detailJob.request_payload || {}, null, 2)}</pre></div>
              <div><h3 className="mb-2 font-bold text-gray-900">Result summary</h3><pre className="max-h-64 overflow-auto rounded-xl bg-gray-950 p-4 text-xs text-gray-100">{JSON.stringify(detailJob.result_summary || {}, null, 2)}</pre></div>
            </section>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default GenerationJobsPage;
